/**
 * ====================================================================
 * FIKO CAD DIMENSION ANIMATOR
 * ====================================================================
 *
 * Dimension animasyonları yöneticisi
 * Fade, pulse, snap indicators, drag feedback
 *
 * @author FikoYT CAD Team
 * @version 2.0.0
 */

import * as THREE from 'three';
import { AnimationConfig } from './DimensionVisualStyle';
import { Dimension } from '../DimensionTypes';

// ====================================================================
// EASING FUNCTIONS
// ====================================================================

/**
 * Easing fonksiyonları
 */
export const Easing = {
    linear: (t: number): number => t,

    easeIn: (t: number): number => t * t,

    easeOut: (t: number): number => t * (2 - t),

    easeInOut: (t: number): number =>
        t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t,

    easeInQuad: (t: number): number => t * t,

    easeOutQuad: (t: number): number => t * (2 - t),

    easeInOutQuad: (t: number): number =>
        t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t,

    easeInCubic: (t: number): number => t * t * t,

    easeOutCubic: (t: number): number => (--t) * t * t + 1,

    easeInOutCubic: (t: number): number =>
        t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1,

    easeInSine: (t: number): number => 1 - Math.cos((t * Math.PI) / 2),

    easeOutSine: (t: number): number => Math.sin((t * Math.PI) / 2),

    easeInOutSine: (t: number): number => -(Math.cos(Math.PI * t) - 1) / 2,

    elastic: (t: number): number => {
        const c4 = (2 * Math.PI) / 3;
        return t === 0
            ? 0
            : t === 1
                ? 1
                : Math.pow(2, -10 * t) * Math.sin((t * 10 - 0.75) * c4) + 1;
    },

    bounce: (t: number): number => {
        const n1 = 7.5625;
        const d1 = 2.75;

        if (t < 1 / d1) {
            return n1 * t * t;
        } else if (t < 2 / d1) {
            return n1 * (t -= 1.5 / d1) * t + 0.75;
        } else if (t < 2.5 / d1) {
            return n1 * (t -= 2.25 / d1) * t + 0.9375;
        } else {
            return n1 * (t -= 2.625 / d1) * t + 0.984375;
        }
    }
};

/**
 * Easing tipi
 */
export type EasingType = keyof typeof Easing;

/**
 * Easing fonksiyonu al
 */
function getEasing(type: EasingType | string): (t: number) => number {
    return Easing[type as EasingType] || Easing.easeOut;
}

// ====================================================================
// ANIMATION CLASS
// ====================================================================

/**
 * Animasyon tanımı
 */
interface Animation {
    id: string;
    startTime: number;
    duration: number;
    easing: (t: number) => number;
    update: (progress: number) => void;
    onComplete?: () => void;
}

/**
 * Dimension Animator - Animasyon yöneticisi
 */
export class DimensionAnimator {
    // Aktif animasyonlar
    private animations = new Map<string, Animation>();

    // RAF handle
    private rafHandle: number | null = null;

    // Singleton instance
    private static instance: DimensionAnimator;

    // Konfigürasyon
    private config: AnimationConfig;

    private constructor() {
        this.config = this.getDefaultConfig();
    }

    /**
     * Singleton instance al
     */
    static getInstance(): DimensionAnimator {
        if (!this.instance) {
            this.instance = new DimensionAnimator();
        }
        return this.instance;
    }

    /**
     * Varsayılan konfigürasyon
     */
    private getDefaultConfig(): AnimationConfig {
        return {
            enabled: true,
            fadeInDuration: 250,
            fadeOutDuration: 180,
            hoverTransitionDuration: 120,
            dragFeedbackScale: 1.08,
            snapIndicatorDuration: 250,
            pulseOnChange: true,
            pulseDuration: 500,
            pulseColor: 0x27AE60,
            easing: 'easeOut'
        };
    }

    /**
     * Konfigürasyonu güncelle
     */
    setConfig(config: Partial<AnimationConfig>): void {
        this.config = { ...this.config, ...config };
    }

    /**
     * Animasyon etkin mi?
     */
    isEnabled(): boolean {
        return this.config.enabled;
    }

    /**
     * Animasyonları etkinleştir/devre dışı bırak
     */
    setEnabled(enabled: boolean): void {
        this.config.enabled = enabled;
        if (!enabled) {
            this.cancelAll();
        }
    }

    // ====================================================================
    // CORE ANIMATIONS
    // ====================================================================

    /**
     * Fade in animasyonu
     */
    fadeIn(
        dimension: Dimension,
        duration?: number,
        easing?: EasingType
    ): Promise<void> {
        if (!this.config.enabled) {
            this.setOpacity(dimension, 1);
            return Promise.resolve();
        }

        return new Promise(resolve => {
            const id = `fadeIn_${dimension.userData.dimensionId}`;

            // Başlangıç durumu
            this.setOpacity(dimension, 0);

            this.animations.set(id, {
                id,
                startTime: performance.now(),
                duration: duration || this.config.fadeInDuration,
                easing: getEasing(easing || this.config.easing),
                update: (progress: number) => {
                    this.setOpacity(dimension, progress);
                },
                onComplete: () => {
                    this.animations.delete(id);
                    resolve();
                }
            });

            this.startAnimationLoop();
        });
    }

    /**
     * Fade out animasyonu
     * @param dimension Dimension objesi
     * @param durationOrCallback Süre veya callback fonksiyonu
     * @param easingOrCallback Easing veya callback fonksiyonu
     */
    fadeOut(
        dimension: Dimension,
        durationOrCallback?: number | (() => void),
        easingOrCallback?: EasingType | (() => void)
    ): Promise<void> {
        // Parametreleri çöz
        let duration: number | undefined;
        let easing: EasingType | undefined;
        let callback: (() => void) | undefined;

        if (typeof durationOrCallback === 'function') {
            callback = durationOrCallback;
        } else {
            duration = durationOrCallback;
        }

        if (typeof easingOrCallback === 'function') {
            callback = easingOrCallback;
        } else {
            easing = easingOrCallback;
        }

        if (!this.config.enabled) {
            this.setOpacity(dimension, 0);
            callback?.();
            return Promise.resolve();
        }

        return new Promise(resolve => {
            const id = `fadeOut_${dimension.userData.dimensionId}`;

            this.animations.set(id, {
                id,
                startTime: performance.now(),
                duration: duration || this.config.fadeOutDuration,
                easing: getEasing(easing || this.config.easing),
                update: (progress: number) => {
                    this.setOpacity(dimension, 1 - progress);
                },
                onComplete: () => {
                    this.animations.delete(id);
                    callback?.();
                    resolve();
                }
            });

            this.startAnimationLoop();
        });
    }

    /**
     * Pulse animasyonu (değer değişikliği için)
     */
    pulse(
        dimension: Dimension,
        pulseColor?: number,
        duration?: number
    ): void {
        if (!this.config.enabled || !this.config.pulseOnChange) return;

        const id = `pulse_${dimension.userData.dimensionId}`;
        const originalColors = this.captureColors(dimension);
        const targetColor = new THREE.Color(pulseColor || this.config.pulseColor);

        this.animations.set(id, {
            id,
            startTime: performance.now(),
            duration: duration || this.config.pulseDuration,
            easing: Easing.easeInOutSine,
            update: (progress: number) => {
                // 0 -> 1 -> 0 pulse
                const pulseProgress = Math.sin(progress * Math.PI);

                dimension.traverse(child => {
                    if (child instanceof THREE.Mesh || child instanceof THREE.Line) {
                        const material = child.material as THREE.MeshBasicMaterial | THREE.LineBasicMaterial;
                        const originalColor = originalColors.get(child.uuid);

                        if (originalColor && material.color) {
                            material.color.lerpColors(
                                originalColor,
                                targetColor,
                                pulseProgress
                            );
                        }
                    }
                });
            },
            onComplete: () => {
                this.restoreColors(dimension, originalColors);
                this.animations.delete(id);
            }
        });

        this.startAnimationLoop();
    }

    /**
     * Hover geçiş animasyonu
     * @param dimension Dimension objesi
     * @param isHovering Hover durumu
     * @param hoverColor Hover rengi (opsiyonel, varsayılan: 0xFF6600)
     * @param normalColor Normal renk (opsiyonel, varsayılan: 0xFFFFFF)
     */
    hoverTransition(
        dimension: Dimension,
        isHovering: boolean,
        hoverColor: number = 0xFF6600,
        normalColor: number = 0xFFFFFF
    ): void {
        if (!this.config.enabled) {
            this.setColor(dimension, isHovering ? hoverColor : normalColor);
            return;
        }

        const id = `hover_${dimension.userData.dimensionId}`;
        const fromColor = new THREE.Color(isHovering ? normalColor : hoverColor);
        const toColor = new THREE.Color(isHovering ? hoverColor : normalColor);

        // Mevcut hover animasyonunu iptal et
        this.animations.delete(id);

        this.animations.set(id, {
            id,
            startTime: performance.now(),
            duration: this.config.hoverTransitionDuration,
            easing: Easing.easeOut,
            update: (progress: number) => {
                const currentColor = new THREE.Color();
                currentColor.lerpColors(fromColor, toColor, progress);
                this.setColorFromThreeColor(dimension, currentColor);
            },
            onComplete: () => {
                this.animations.delete(id);
            }
        });

        this.startAnimationLoop();
    }

    /**
     * Drag feedback animasyonu
     */
    dragFeedback(dimension: Dimension, isDragging: boolean): void {
        const targetOpacity = isDragging ? 0.7 : 1.0;
        const targetScale = isDragging ? this.config.dragFeedbackScale : 1.0;

        // Smooth lerp (animasyon döngüsü dışında, her frame çağrılabilir)
        dimension.traverse(child => {
            if (child instanceof THREE.Mesh || child instanceof THREE.Sprite) {
                const material = child.material as THREE.Material;
                if (material.opacity !== undefined) {
                    material.opacity += (targetOpacity - material.opacity) * 0.2;
                    material.transparent = true;
                }
            }
        });

        // Scale lerp
        const currentScale = dimension.scale.x;
        const newScale = currentScale + (targetScale - currentScale) * 0.2;
        dimension.scale.setScalar(newScale);
    }

    /**
     * Snap indicator animasyonu
     * @param position Snap pozisyonu
     * @param sceneOrColor THREE.Scene veya renk (hex)
     * @param duration Süre (ms)
     * @param color Renk (hex)
     */
    snapIndicator(
        position: THREE.Vector3,
        sceneOrColor?: THREE.Scene | number,
        duration?: number,
        color?: number
    ): void {
        if (!this.config.enabled) return;

        // Parametreleri çözümle
        let scene: THREE.Scene | null = null;
        let snapColor: number;
        let snapDuration: number;

        if (typeof sceneOrColor === 'number') {
            // sceneOrColor aslında renk
            snapColor = sceneOrColor;
            snapDuration = duration || this.config.snapIndicatorDuration;
        } else {
            scene = sceneOrColor || null;
            snapDuration = duration || this.config.snapIndicatorDuration;
            snapColor = color || 0x000000; // 🎯 SİYAH
        }

        // Scene yoksa sadece console log
        if (!scene) {
            // console.log('SnapIndicator:', position, snapColor);
            return;
        }

        // Geçici snap ring oluştur
        const ringGeometry = new THREE.RingGeometry(0.3, 0.4, 32);
        const ringMaterial = new THREE.MeshBasicMaterial({
            color: snapColor,
            transparent: true,
            opacity: 1,
            side: THREE.DoubleSide,
            depthTest: false
        });

        const ring = new THREE.Mesh(ringGeometry, ringMaterial);
        ring.position.copy(position);
        ring.renderOrder = Infinity;
        scene.add(ring);

        const id = `snap_${Date.now()}`;

        this.animations.set(id, {
            id,
            startTime: performance.now(),
            duration: snapDuration,
            easing: Easing.easeOut,
            update: (progress: number) => {
                // Scale up + fade out
                const scale = 1 + progress * 2;
                ring.scale.setScalar(scale);
                ringMaterial.opacity = 1 - progress;
            },
            onComplete: () => {
                scene.remove(ring);
                ringGeometry.dispose();
                ringMaterial.dispose();
                this.animations.delete(id);
            }
        });

        this.startAnimationLoop();
    }

    /**
     * Scale animasyonu
     */
    animateScale(
        dimension: Dimension,
        fromScale: number,
        toScale: number,
        duration: number,
        easing?: EasingType
    ): Promise<void> {
        if (!this.config.enabled) {
            dimension.scale.setScalar(toScale);
            return Promise.resolve();
        }

        return new Promise(resolve => {
            const id = `scale_${dimension.userData.dimensionId}`;

            this.animations.set(id, {
                id,
                startTime: performance.now(),
                duration,
                easing: getEasing(easing || 'easeOut'),
                update: (progress: number) => {
                    const scale = fromScale + (toScale - fromScale) * progress;
                    dimension.scale.setScalar(scale);
                },
                onComplete: () => {
                    this.animations.delete(id);
                    resolve();
                }
            });

            this.startAnimationLoop();
        });
    }

    /**
     * Position animasyonu
     */
    animatePosition(
        dimension: Dimension,
        fromPosition: THREE.Vector3,
        toPosition: THREE.Vector3,
        duration: number,
        easing?: EasingType
    ): Promise<void> {
        if (!this.config.enabled) {
            dimension.position.copy(toPosition);
            return Promise.resolve();
        }

        return new Promise(resolve => {
            const id = `position_${dimension.userData.dimensionId}`;

            this.animations.set(id, {
                id,
                startTime: performance.now(),
                duration,
                easing: getEasing(easing || 'easeOut'),
                update: (progress: number) => {
                    dimension.position.lerpVectors(fromPosition, toPosition, progress);
                },
                onComplete: () => {
                    this.animations.delete(id);
                    resolve();
                }
            });

            this.startAnimationLoop();
        });
    }

    // ====================================================================
    // HELPER METHODS
    // ====================================================================

    /**
     * Opacity ayarla
     */
    private setOpacity(dimension: Dimension, opacity: number): void {
        dimension.traverse(child => {
            if (child instanceof THREE.Mesh || child instanceof THREE.Sprite || child instanceof THREE.Line) {
                const material = child.material as THREE.Material;
                material.transparent = true;
                material.opacity = opacity;
            }
        });
    }

    /**
     * Renk ayarla
     */
    private setColor(dimension: Dimension, color: number): void {
        const threeColor = new THREE.Color(color);
        this.setColorFromThreeColor(dimension, threeColor);
    }

    /**
     * THREE.Color ile renk ayarla
     */
    private setColorFromThreeColor(dimension: Dimension, color: THREE.Color): void {
        dimension.traverse(child => {
            if (child instanceof THREE.Mesh || child instanceof THREE.Line) {
                const material = child.material as THREE.MeshBasicMaterial | THREE.LineBasicMaterial;
                if (material.color) {
                    material.color.copy(color);
                }
            }
        });
    }

    /**
     * Renkleri yakala
     */
    private captureColors(dimension: Dimension): Map<string, THREE.Color> {
        const colors = new Map<string, THREE.Color>();

        dimension.traverse(child => {
            if (child instanceof THREE.Mesh || child instanceof THREE.Line) {
                const material = child.material as THREE.MeshBasicMaterial | THREE.LineBasicMaterial;
                if (material.color) {
                    colors.set(child.uuid, material.color.clone());
                }
            }
        });

        return colors;
    }

    /**
     * Renkleri geri yükle
     */
    private restoreColors(dimension: Dimension, colors: Map<string, THREE.Color>): void {
        dimension.traverse(child => {
            if (child instanceof THREE.Mesh || child instanceof THREE.Line) {
                const material = child.material as THREE.MeshBasicMaterial | THREE.LineBasicMaterial;
                const originalColor = colors.get(child.uuid);
                if (originalColor && material.color) {
                    material.color.copy(originalColor);
                }
            }
        });
    }

    /**
     * Animasyon döngüsünü başlat
     */
    private startAnimationLoop(): void {
        if (this.rafHandle !== null) return;

        const loop = () => {
            const now = performance.now();

            this.animations.forEach((anim, id) => {
                const elapsed = now - anim.startTime;
                const rawProgress = Math.min(elapsed / anim.duration, 1);
                const easedProgress = anim.easing(rawProgress);

                anim.update(easedProgress);

                if (rawProgress >= 1) {
                    anim.onComplete?.();
                }
            });

            if (this.animations.size > 0) {
                this.rafHandle = requestAnimationFrame(loop);
            } else {
                this.rafHandle = null;
            }
        };

        this.rafHandle = requestAnimationFrame(loop);
    }

    /**
     * Belirli bir animasyonu iptal et
     */
    cancel(animationId: string): void {
        this.animations.delete(animationId);
    }

    /**
     * Dimension için tüm animasyonları iptal et
     */
    cancelForDimension(dimension: Dimension): void {
        const dimensionId = dimension.userData.dimensionId;
        const toDelete: string[] = [];

        this.animations.forEach((_, id) => {
            if (id.includes(dimensionId)) {
                toDelete.push(id);
            }
        });

        toDelete.forEach(id => this.animations.delete(id));
    }

    /**
     * Tüm animasyonları iptal et
     */
    cancelAll(): void {
        this.animations.clear();

        if (this.rafHandle !== null) {
            cancelAnimationFrame(this.rafHandle);
            this.rafHandle = null;
        }
    }

    /**
     * Aktif animasyon sayısı
     */
    getActiveCount(): number {
        return this.animations.size;
    }
}

// ====================================================================
// EXPORTS
// ====================================================================

export type { AnimationConfig };
